import api from './axiosInstance';

export interface ProjectType {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  category: 'wind' | 'solar' | 'hybrid' | 'bess';
  status: 'active' | 'inactive';
  projectTypeStatus?: 'active' | 'inactive';
  sequence?: number;
  displayNumber?: string;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProjectTypeFilters {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  status?: 'active' | 'inactive';
  category?: 'wind' | 'solar' | 'hybrid' | 'bess';
}

export interface ProjectTypeDropdownOption {
  value: string;
  label: string;
}

class ProjectTypeService {
  /**
   * Get project types with pagination and filters
   */
  async getProjectTypes(filters: ProjectTypeFilters = {}) {
    try {
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);

      const response = await api.get(`/project-types?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching project types:', error);
      throw error;
    }
  }

  /**
   * Get all active project types for dropdown
   */
  async getProjectTypesForDropdown(): Promise<ProjectTypeDropdownOption[]> {
    try {
      console.log('🌐 Making API call to /project-types/all?status=active');
      const response = await api.get('/project-types/all?status=active');
      console.log('📡 API Response:', response.data);
      
      if (response.data.success && response.data.data) {
        const options = response.data.data.map((projectType: ProjectType) => ({
          value: projectType._id || projectType.id || '',
          label: projectType.name
        }));
        console.log('🎯 Mapped project type options:', options);
        return options;
      }
      
      console.warn('⚠️ No project types found or invalid response format');
      return [];
    } catch (error) {
      console.error('❌ Error fetching project types for dropdown:', error);
      return [];
    }
  }

  /**
   * Get all active project types (full objects for component state)
   */
  async getProjectTypesForComponent(): Promise<ProjectType[]> {
    try {
      console.log('🌐 Making API call to /project-types/all?status=active for component');
      const response = await api.get('/project-types/all?status=active');
      console.log('📡 API Response for component:', response.data);
      
      if (response.data.success && response.data.data) {
        console.log('🎯 Returning full project type objects:', response.data.data);
        return response.data.data as ProjectType[];
      }
      
      console.warn('⚠️ No project types found or invalid response format');
      return [];
    } catch (error) {
      console.error('❌ Error fetching project types for component:', error);
      return [];
    }
  }

  /**
   * Get project type by ID
   */
  async getProjectTypeById(id: string): Promise<ProjectType | null> {
    try {
      const response = await api.get(`/project-types/${id}`);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching project type:', error);
      throw error;
    }
  }

  /**
   * Create new project type
   */
  async createProjectType(data: Partial<ProjectType>): Promise<ProjectType> {
    try {
      const response = await api.post('/project-types', data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to create project type');
    } catch (error) {
      console.error('Error creating project type:', error);
      throw error;
    }
  }

  /**
   * Update project type
   */
  async updateProjectType(id: string, data: Partial<ProjectType>): Promise<ProjectType> {
    try {
      const response = await api.patch(`/project-types/${id}`, data);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to update project type');
    } catch (error) {
      console.error('Error updating project type:', error);
      throw error;
    }
  }

  /**
   * Delete project type
   */
  async deleteProjectType(id: string): Promise<void> {
    try {
      const response = await api.delete(`/project-types/${id}`);
      
      if (!response.data.success) {
        throw new Error('Failed to delete project type');
      }
    } catch (error) {
      console.error('Error deleting project type:', error);
      throw error;
    }
  }

  /**
   * Activate project type
   */
  async activateProjectType(id: string): Promise<ProjectType> {
    try {
      const response = await api.patch(`/project-types/${id}/activate`);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to activate project type');
    } catch (error) {
      console.error('Error activating project type:', error);
      throw error;
    }
  }

  /**
   * Deactivate project type
   */
  async deactivateProjectType(id: string): Promise<ProjectType> {
    try {
      const response = await api.patch(`/project-types/${id}/inactivate`);
      
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
      
      throw new Error('Failed to deactivate project type');
    } catch (error) {
      console.error('Error deactivating project type:', error);
      throw error;
    }
  }

  /**
   * Get all project types (returns full objects for component compatibility)
   */
  async getAllProjectTypes(): Promise<ProjectType[]> {
    console.log('🔍 ProjectTypeService.getAllProjectTypes() called');
    try {
      const result = await this.getProjectTypesForComponent();
      console.log('✅ ProjectTypeService.getAllProjectTypes() success:', result);
      return result;
    } catch (error) {
      console.error('❌ ProjectTypeService.getAllProjectTypes() error:', error);
      throw error;
    }
  }
}

const projectTypeService = new ProjectTypeService();
export default projectTypeService;